<template>
  <v-col
    v-bind="{ ...containerProps, ...containerOptions }"
    class="helmi-chips"
  >
    <helmi-subheader
      v-if="$attrs.label"
      :colors="labelColors || colors"
      :icon="$attrs.icon"
      :label="$attrs.label"
      class="pa-0 mb-n1"
    />

    <v-chip-group
      v-model="basicItems"
      v-bind="$attrs"
      :color="colors.base"
      :mandatory="mandatory"
      :multiple="multiple"
      column
    >
      <v-chip
        v-for="(option, index) in options"
        :key="index"
        filter
        small
        :value="option.value"
        v-text="option.label"
      />
    </v-chip-group>

    <helmi-select
      v-if="showAdditionalItems"
      :id="`${$attrs.id}__additional-items`"
      v-model="additionalItems"
      :label="$attrs['label-additional-options']"
      :multiple="multiple"
      :options="additionalOptions"
      :select-class="`${colors.text} ma-0 pa-0`"
      class="pa-0"
      item-text="label"
      item-value="value"
      chips
      deletable-chips
      dense
      single-line
      small-chips
      solo
    />
  </v-col>
</template>

<script>
import { EventBus } from '@/utils';

const getDefaultState = () => ({
  item: '',
  additionalItems: [],
  basicItems: [],
  defaultValue: [],
  modelValue: [],
});

export default {
  name: 'HelmiChips',

  components: {
    'helmi-subheader': () => import('@/components/formElements/Subheader'),
    'helmi-select': () => import('@/components/formElements/Select'),
  },

  inheritAttrs: false,

  props: {
    additionalOptions: {
      type: Array,
      default: () => [],
    },

    chapter: {
      type: String,
      default: '',
    },

    colors: {
      type: Object,
      default: () => ({
        base: 'primary',
        text: 'primary--text',
      }),
    },

    containerProps: {
      type: Object,
      default: () => ({}),
    },

    containerOptions: {
      type: Object,
      default: () => ({}),
    },

    labelColors: {
      type: Object,
      default: () => null,
    },

    mandatory: {
      type: Boolean,
      default: false,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    numeric: {
      type: Boolean,
      default: false,
    },

    options: {
      type: Array,
      default: () => [],
    },

    propertyValue: {
      type: Array,
      default: () => [],
    },
  },

  data: getDefaultState,

  computed: {
    showAdditionalItems() {
      return this.additionalOptions.length && this.basicItems.includes('other');
    },
  },

  watch: {
    // Handles internal model changes.
    modelValue(newVal) {
      this.$emit('input', newVal);
      this.$emit('evaluate', newVal, this.$attrs.id);
    },

    // Handles external model changes.
    '$attrs.value': {
      handler(val) {
        if (this.multiple) {
          if (!Array.isArray(val) || !(this.val || []).length) return;

          this.basicItems = val;
          return;
        }

        this.modelValue = val;
      },
      immediate: true,
    },

    additionalItems: {
      handler(values) {
        this.modelValue = [...this.basicItems, ...values].sort();
      },
      immediate: true,
    },

    basicItems: {
      handler(values) {
        if (this.multiple) {
          if (!values.includes('other')) {
            this.additionalItems = [];
          }

          this.modelValue = [...this.additionalItems, ...values].sort();
          return;
        }
        this.modelValue = values;
      },
      immediate: true,
    },
  },

  mounted() {
    EventBus.$on('FORM_RESET', this.resetValue);

    this.setInitialValue();
  },

  methods: {
    parseValue(value) {
      if (!this.numeric) return value;

      return parseInt(value, 10);
    },

    resetValue() {
      Object.assign(this.$data, getDefaultState());
    },

    setInitialValue() {
      if (this.$attrs.value && !Array.isArray(this.$attrs.value)) {
        // set value based on db
        if (this.multiple) {
          const items = this.$attrs.value.split(',').map(v => this.parseValue(v));
          this.basicItems = items
            .filter(item => !this.additionalOptions.find(o => o.value === item));

          this.additionalItems = items
            .filter(item => this.additionalOptions.find(o => o.value === item));

          if (this.additionalItems.length) {
            this.basicItems.push('other');
          }
          return;
        }

        this.basicItems = this.parseValue(this.$attrs.value);
        return;
      }

      if (this.mandatory) {
        const { value } = this.options[0];

        if (this.multiple) {
          this.basicItems.push(this.parseValue(value));
          return;
        }

        this.basicItems = this.parseValue(value);
      }
    },
  },
};
</script>

<style lang="scss">
.helmi-chips {
  .v-chip-group .v-chip--active {
    color: inherit !important;
  }

  .v-select--chips {
    .v-chip {
      background: transparent !important;
      color: inherit !important;

      &::before {
        background-color: currentColor;
        bottom: 0;
        border-radius: inherit;
        content: "";
        left: 0;
        opacity: 0.12;
        position: absolute;
        pointer-events: none;
        right: 0;
        top: 0;
      }

      .v-icon {
        color: inherit !important;
      }
    }
  }
}
</style>
